/**
 * SQL Server Analysis Framework
 * Categorization of topics by assessment methodology
 */

export interface AnalysisCategory {
  name: string;
  description: string;
  toolsRequired: string[];
  dataSourcesNeeded: string[];
  assessmentApproach: string;
}

export interface TopicAnalysis {
  topic: string;
  primaryCategory: string;
  secondaryCategories: string[];
  assessmentMethods: {
    static?: string[];
    logical?: string[];
    performance?: string[];
    load?: string[];
    stress?: string[];
  };
  requiredArtifacts: string[];
  automationPotential: 'High' | 'Medium' | 'Low';
  complexity: 'Basic' | 'Intermediate' | 'Advanced' | 'Expert';
}

export const ANALYSIS_CATEGORIES: Record<string, AnalysisCategory> = {
  STATIC: {
    name: 'Static Code Analysis',
    description: 'Assessment without code execution - analyzing source code, schemas, configurations',
    toolsRequired: ['Azure DevOps', 'SQL Parser', 'Schema Analysis Tools', 'Code Quality Tools'],
    dataSourcesNeeded: ['Source Code Repository', 'Database Schema DDL', 'Configuration Files', 'Stored Procedures/Functions'],
    assessmentApproach: 'Parse and analyze code structure, patterns, and configurations without execution'
  },
  
  LOGICAL: {
    name: 'Logical Design Analysis', 
    description: 'Assessment of database design, architecture patterns, and logical structures',
    toolsRequired: ['ERD Tools', 'Schema Analyzers', 'Design Pattern Validators', 'Normalization Checkers'],
    dataSourcesNeeded: ['Database Schema', 'Relationship Definitions', 'Index Definitions', 'Constraint Definitions'],
    assessmentApproach: 'Analyze logical design principles, normalization, relationships, and architectural patterns'
  },

  PERFORMANCE: {
    name: 'Performance Monitoring Analysis',
    description: 'Assessment using existing production metrics and performance counters', 
    toolsRequired: ['Performance Monitor', 'DMV Queries', 'Query Store', 'Wait Statistics Tools'],
    dataSourcesNeeded: ['Performance Counters', 'Wait Statistics', 'Query Store Data', 'Execution Plans'],
    assessmentApproach: 'Analyze existing system performance data and metrics from production'
  },

  LOAD: {
    name: 'Load Profile Analysis',
    description: 'Assessment under controlled load scenarios mimicking production patterns',
    toolsRequired: ['Load Testing Tools', 'Workload Generators', 'Profiling Tools', 'Resource Monitors'],
    dataSourcesNeeded: ['Production Workload Patterns', 'User Concurrency Data', 'Transaction Volumes'],
    assessmentApproach: 'Execute controlled load tests with realistic workload patterns'
  },

  STRESS: {
    name: 'Stress Testing Analysis', 
    description: 'Assessment under extreme conditions to find breaking points and limits',
    toolsRequired: ['Stress Testing Tools', 'Resource Exhaustion Tools', 'Failure Injection Tools'],
    dataSourcesNeeded: ['System Capacity Limits', 'Resource Constraints', 'Failure Scenarios'],
    assessmentApproach: 'Push system beyond normal limits to identify bottlenecks and failure modes'
  }
};

export const SQL_TOPICS_ANALYSIS: TopicAnalysis[] = [
  // ==== CONFIGURATION & ADMINISTRATION TOPICS ====
  {
    topic: 'Advanced Query Store Configuration',
    primaryCategory: 'STATIC',
    secondaryCategories: ['PERFORMANCE'],
    assessmentMethods: {
      static: ['Configuration file analysis', 'Query Store settings review', 'Retention policy validation'],
      performance: ['Query Store metrics analysis', 'Plan forcing effectiveness review']
    },
    requiredArtifacts: ['Configuration files', 'Query Store DMV data'],
    automationPotential: 'High',
    complexity: 'Advanced'
  },

  {
    topic: 'Intelligent Query Processing',
    primaryCategory: 'PERFORMANCE',
    secondaryCategories: ['LOAD'],
    assessmentMethods: {
      performance: ['Adaptive join analysis', 'Memory grant feedback review', 'Batch mode effectiveness'],
      load: ['IQP feature impact under load', 'Adaptive behavior validation']
    },
    requiredArtifacts: ['Execution plans', 'Query Store data', 'Performance counters'],
    automationPotential: 'Medium',
    complexity: 'Expert'
  },

  {
    topic: 'SQL Server Architecture Scheduling and Waits',
    primaryCategory: 'PERFORMANCE',
    secondaryCategories: ['STRESS'],
    assessmentMethods: {
      performance: ['Wait statistics analysis', 'Scheduler health DMVs', 'SQLOS counters review'],
      stress: ['Scheduler stress under high CPU', 'Wait type patterns under pressure']
    },
    requiredArtifacts: ['Wait statistics', 'Scheduler DMVs', 'Performance counters'],
    automationPotential: 'High',
    complexity: 'Expert'
  },

  // ==== DATABASE DESIGN TOPICS ====
  {
    topic: 'Columnstore Index Optimization',
    primaryCategory: 'LOGICAL',
    secondaryCategories: ['STATIC', 'LOAD'],
    assessmentMethods: {
      static: ['Columnstore suitability analysis', 'Data type compatibility check'],
      logical: ['Workload pattern analysis', 'Compression benefit estimation'],
      load: ['Analytical query performance testing', 'Compression effectiveness under load']
    },
    requiredArtifacts: ['Table schemas', 'Query patterns', 'Data distribution stats'],
    automationPotential: 'Medium',
    complexity: 'Advanced'
  },

  {
    topic: 'In-Memory OLTP (Hekaton)',
    primaryCategory: 'LOGICAL',
    secondaryCategories: ['STATIC', 'STRESS'],
    assessmentMethods: {
      static: ['Memory-optimized table candidate analysis', 'Code migration assessment'],
      logical: ['OLTP workload suitability evaluation', 'Memory requirement estimation'],
      stress: ['Memory pressure testing', 'High-concurrency validation']
    },
    requiredArtifacts: ['Table schemas', 'OLTP workload patterns', 'Memory configuration'],
    automationPotential: 'Low',
    complexity: 'Expert'
  },

  {
    topic: 'Statistics and Index Internals',
    primaryCategory: 'STATIC',
    secondaryCategories: ['LOGICAL', 'PERFORMANCE'],
    assessmentMethods: {
      static: ['Index definition analysis', 'Statistics metadata review', 'Cardinality estimation validation'],
      logical: ['Index design pattern evaluation', 'Statistics strategy assessment'],
      performance: ['Index usage statistics', 'Cardinality estimation accuracy']
    },
    requiredArtifacts: ['Index definitions', 'Statistics metadata', 'Query patterns'],
    automationPotential: 'High',
    complexity: 'Advanced'
  },

  {
    topic: 'Index Tuning',
    primaryCategory: 'PERFORMANCE',
    secondaryCategories: ['STATIC', 'LOAD'],
    assessmentMethods: {
      static: ['Missing index recommendations', 'Duplicate index detection', 'Index usage analysis'],
      performance: ['Index effectiveness metrics', 'Seek vs scan ratios'],
      load: ['Index performance under realistic workload', 'Maintenance overhead assessment']
    },
    requiredArtifacts: ['Index definitions', 'Query execution plans', 'Index usage DMVs'],
    automationPotential: 'High',
    complexity: 'Intermediate'
  },

  // ==== SECURITY TOPICS ====
  {
    topic: 'Advanced Threat Protection',
    primaryCategory: 'STATIC',
    secondaryCategories: ['PERFORMANCE'],
    assessmentMethods: {
      static: ['Security configuration review', 'Vulnerability assessment scan', 'Data classification audit'],
      performance: ['ATP overhead analysis', 'Security feature performance impact']
    },
    requiredArtifacts: ['Security configurations', 'ATP reports', 'Audit logs'],
    automationPotential: 'High',
    complexity: 'Advanced'
  },

  {
    topic: 'Always Encrypted & Dynamic Data Masking',
    primaryCategory: 'STATIC',
    secondaryCategories: ['LOGICAL', 'PERFORMANCE'],
    assessmentMethods: {
      static: ['Encryption configuration analysis', 'Masking rule validation', 'Key management review'],
      logical: ['Data sensitivity classification', 'Masking strategy evaluation'],
      performance: ['Encryption/decryption overhead', 'Query performance impact']
    },
    requiredArtifacts: ['Encryption keys', 'Masking configurations', 'Data classification'],
    automationPotential: 'Medium',
    complexity: 'Advanced'
  },

  // ==== PERFORMANCE TOPICS ====
  {
    topic: 'CPU Performance Monitoring & Tuning',
    primaryCategory: 'PERFORMANCE',
    secondaryCategories: ['LOAD', 'STRESS'],
    assessmentMethods: {
      performance: ['CPU utilization analysis', 'Parallelism effectiveness review', 'NUMA node distribution'],
      load: ['CPU scaling under increased load', 'Parallelism behavior validation'],
      stress: ['CPU saturation testing', 'Scheduler contention analysis']
    },
    requiredArtifacts: ['CPU performance counters', 'Wait statistics', 'Parallelism metrics'],
    automationPotential: 'High',
    complexity: 'Advanced'
  },

  {
    topic: 'SQL Server Tempdb Internals and Tuning',
    primaryCategory: 'PERFORMANCE',
    secondaryCategories: ['LOGICAL', 'STRESS'],
    assessmentMethods: {
      performance: ['Tempdb usage patterns', 'Allocation contention analysis', 'File growth patterns'],
      logical: ['Tempdb configuration assessment', 'File placement strategy'],
      stress: ['Tempdb exhaustion testing', 'High-concurrency tempdb stress']
    },
    requiredArtifacts: ['Tempdb configuration', 'Allocation DMVs', 'Tempdb usage metrics'],
    automationPotential: 'Medium',
    complexity: 'Advanced'
  },

  {
    topic: 'SQL Server IO Internals and Tuning',
    primaryCategory: 'PERFORMANCE',
    secondaryCategories: ['STRESS', 'LOAD'],
    assessmentMethods: {
      performance: ['IO wait analysis', 'Disk performance counters', 'File IO distribution'],
      load: ['IO scaling under load', 'Storage performance validation'],
      stress: ['IO subsystem saturation', 'Disk failure scenarios']
    },
    requiredArtifacts: ['IO performance counters', 'Wait statistics', 'Disk configuration'],
    automationPotential: 'High',
    complexity: 'Advanced'
  },

  {
    topic: 'SQL Server Memory Internals and Troubleshooting',
    primaryCategory: 'PERFORMANCE',
    secondaryCategories: ['STRESS', 'LOGICAL'],
    assessmentMethods: {
      performance: ['Memory clerk analysis', 'Buffer pool efficiency', 'Memory grant patterns'],
      stress: ['Memory pressure testing', 'Out-of-memory scenarios'],
      logical: ['Memory configuration assessment', 'Memory allocation strategy']
    },
    requiredArtifacts: ['Memory DMVs', 'Memory grants', 'Memory configuration'],
    automationPotential: 'High',
    complexity: 'Expert'
  },

  {
    topic: 'Concurrency & Transactions',
    primaryCategory: 'PERFORMANCE',
    secondaryCategories: ['STRESS', 'LOAD'],
    assessmentMethods: {
      performance: ['Lock analysis', 'Deadlock pattern review', 'Blocking chain analysis'],
      load: ['Concurrency behavior under load', 'Transaction throughput analysis'],
      stress: ['High-concurrency stress testing', 'Deadlock frequency under pressure']
    },
    requiredArtifacts: ['Lock DMVs', 'Deadlock graphs', 'Blocking reports'],
    automationPotential: 'Medium',
    complexity: 'Expert'
  },

  {
    topic: 'Query Execution & Query Plan Analysis',
    primaryCategory: 'STATIC',
    secondaryCategories: ['PERFORMANCE', 'LOAD'],
    assessmentMethods: {
      static: ['Execution plan structure analysis', 'Operator cost evaluation', 'Plan quality assessment'],
      performance: ['Plan cache analysis', 'Actual vs estimated rows', 'Runtime metrics'],
      load: ['Plan stability under load', 'Parameter sniffing impact']
    },
    requiredArtifacts: ['Execution plans', 'Query Store data', 'Plan cache DMVs'],
    automationPotential: 'High',
    complexity: 'Advanced'
  },

  {
    topic: 'Query Tuning',
    primaryCategory: 'STATIC',
    secondaryCategories: ['PERFORMANCE', 'LOAD'],
    assessmentMethods: {
      static: ['Query structure analysis', 'Syntax optimization review', 'Join pattern evaluation'],
      performance: ['Query performance metrics', 'Resource consumption analysis'],
      load: ['Query performance under load', 'Scaling behavior validation']
    },
    requiredArtifacts: ['Query text', 'Execution plans', 'Performance metrics'],
    automationPotential: 'Medium',
    complexity: 'Intermediate'
  },

  {
    topic: 'Plan Caching & Recompilation',
    primaryCategory: 'PERFORMANCE',
    secondaryCategories: ['STATIC', 'LOAD'],
    assessmentMethods: {
      static: ['Plan cache configuration review', 'Recompilation trigger analysis'],
      performance: ['Plan reuse metrics', 'Compilation overhead analysis', 'Cache hit ratios'],
      load: ['Plan cache behavior under load', 'Memory pressure impact on cache']
    },
    requiredArtifacts: ['Plan cache DMVs', 'Compilation metrics', 'Memory configuration'],
    automationPotential: 'High',
    complexity: 'Advanced'
  },

  {
    topic: 'Monitoring Tracing & Baselining',
    primaryCategory: 'PERFORMANCE',
    secondaryCategories: ['LOGICAL'],
    assessmentMethods: {
      performance: ['Baseline establishment', 'Trend analysis', 'Performance deviation detection'],
      logical: ['Monitoring strategy assessment', 'KPI definition validation']
    },
    requiredArtifacts: ['Historical performance data', 'Monitoring configurations', 'Alerting rules'],
    automationPotential: 'High',
    complexity: 'Intermediate'
  },

  // ==== DIAGNOSTICS & MONITORING TOPICS ====
  {
    topic: 'Extended Events & Advanced Diagnostics',
    primaryCategory: 'PERFORMANCE',
    secondaryCategories: ['STATIC', 'LOAD'],
    assessmentMethods: {
      static: ['XE session configuration review', 'Event selection validation'],
      performance: ['XE data analysis', 'Event correlation analysis'],
      load: ['XE overhead assessment', 'High-volume event handling']
    },
    requiredArtifacts: ['XE session definitions', 'XE data files', 'Event metadata'],
    automationPotential: 'Medium',
    complexity: 'Advanced'
  },

  // ==== HIGH AVAILABILITY TOPICS ====
  {
    topic: 'Distributed Availability Groups',
    primaryCategory: 'LOGICAL',
    secondaryCategories: ['PERFORMANCE', 'STRESS'],
    assessmentMethods: {
      logical: ['AG architecture assessment', 'Network topology validation', 'Replica configuration review'],
      performance: ['Synchronization performance', 'Network latency impact'],
      stress: ['Failover testing', 'Network partition scenarios']
    },
    requiredArtifacts: ['AG configurations', 'Network topology', 'Replica health DMVs'],
    automationPotential: 'Low',
    complexity: 'Expert'
  },

  {
    topic: 'Advanced HA/DR Strategies',
    primaryCategory: 'LOGICAL',
    secondaryCategories: ['STRESS'],
    assessmentMethods: {
      logical: ['DR strategy evaluation', 'RTO/RPO assessment', 'Cross-platform compatibility'],
      stress: ['Disaster scenario testing', 'Failover validation', 'Recovery time testing']
    },
    requiredArtifacts: ['DR documentation', 'HA configurations', 'Failover procedures'],
    automationPotential: 'Low',
    complexity: 'Expert'
  },

  // ==== MAINTENANCE TOPICS ====
  {
    topic: 'Intelligent Database Maintenance',
    primaryCategory: 'LOGICAL',
    secondaryCategories: ['PERFORMANCE'],
    assessmentMethods: {
      logical: ['Maintenance strategy assessment', 'Automation workflow review'],
      performance: ['Maintenance overhead analysis', 'Window optimization']
    },
    requiredArtifacts: ['Maintenance plans', 'Job configurations', 'Maintenance history'],
    automationPotential: 'High',
    complexity: 'Intermediate'
  },

  {
    topic: 'Advanced Backup Strategies',
    primaryCategory: 'LOGICAL',
    secondaryCategories: ['STRESS'],
    assessmentMethods: {
      logical: ['Backup strategy evaluation', 'Recovery model assessment', 'Retention policy review'],
      stress: ['Backup performance under load', 'Recovery time testing']
    },
    requiredArtifacts: ['Backup configurations', 'Recovery procedures', 'Backup history'],
    automationPotential: 'High',
    complexity: 'Advanced'
  },

  // ==== PLATFORM & CLOUD TOPICS ====
  {
    topic: 'SQL Server on Linux & Containers',
    primaryCategory: 'LOGICAL',
    secondaryCategories: ['PERFORMANCE', 'STRESS'],
    assessmentMethods: {
      logical: ['Platform compatibility assessment', 'Container orchestration review'],
      performance: ['Cross-platform performance comparison', 'Container overhead analysis'],
      stress: ['Container resource limits testing', 'Kubernetes scaling validation']
    },
    requiredArtifacts: ['Container configurations', 'Platform specifications', 'Orchestration manifests'],
    automationPotential: 'Medium',
    complexity: 'Advanced'
  },

  {
    topic: 'Machine Learning & Advanced Analytics',
    primaryCategory: 'STATIC',
    secondaryCategories: ['PERFORMANCE', 'LOAD'],
    assessmentMethods: {
      static: ['ML Services configuration review', 'R/Python script analysis'],
      performance: ['ML workload performance analysis', 'Resource utilization during training'],
      load: ['Concurrent ML job performance', 'Analytics workload scaling']
    },
    requiredArtifacts: ['ML Services config', 'R/Python scripts', 'Analytics workload patterns'],
    automationPotential: 'Low',
    complexity: 'Expert'
  },

  {
    topic: 'Cloud Hybrid & Azure Integration',
    primaryCategory: 'LOGICAL',
    secondaryCategories: ['PERFORMANCE', 'STRESS'],
    assessmentMethods: {
      logical: ['Hybrid architecture assessment', 'Azure service integration review'],
      performance: ['Hybrid workload performance', 'Network latency analysis'],
      stress: ['Cloud failover testing', 'Hybrid connectivity resilience']
    },
    requiredArtifacts: ['Hybrid configurations', 'Azure service definitions', 'Network topology'],
    automationPotential: 'Medium',
    complexity: 'Expert'
  }
];

// Helper functions for categorization
export const getTopicsByCategory = (category: string): TopicAnalysis[] => {
  return SQL_TOPICS_ANALYSIS.filter(topic => 
    topic.primaryCategory === category || topic.secondaryCategories.includes(category)
  );
};

export const getAssessmentMethodsForTopic = (topicName: string): TopicAnalysis | undefined => {
  return SQL_TOPICS_ANALYSIS.find(topic => topic.topic === topicName);
};

export const getTopicsByComplexity = (complexity: string): TopicAnalysis[] => {
  return SQL_TOPICS_ANALYSIS.filter(topic => topic.complexity === complexity);
};

export const getTopicsByAutomationPotential = (potential: string): TopicAnalysis[] => {
  return SQL_TOPICS_ANALYSIS.filter(topic => topic.automationPotential === potential);
};